import { useState } from "react";

import Accordion from "@vfuk/core-accordion";
import {
  CollapsibleContainer,
  CollapsibleContainerBody,
  CollapsibleContainerHeader,
} from "@vfuk/core-collapsible-container";
import CardContainer from "@vfuk/core-container";
import Paragraph from "@vfuk/core-paragraph";
import SimpleGrid from "@vfuk/core-simple-grid";
import Spacing from "@vfuk/core-spacing/";

const ORDER_ARRAY = ["Manage users", "Manage customers", "Manage roles"];
const ORDER_MAP = new Map(ORDER_ARRAY.map((item, index) => [item, index]));

export type AccordionBoxProps = {
  matrixType: string;
  matrix: {
    [key1: string]: {
      display: string;
      permissions: {
        [key2: string]: {
          display: string;
          options: {
            [optionKey: string]: { selected: boolean; enabled: boolean };
          };
        };
      };
    };
  };
  handlePermissionChanges: (returnMatrix: object, matrixType: string) => void;
};

export const AccordionBox = (props: AccordionBoxProps): JSX.Element => {
  const [mainMatrix, setMainMatrix] = useState(props.matrix);

  const addProductPermission = (
    // When a permission is changed emit it to caller function and change the mainMatrix
    key1: string,
    key2: string,
    permissionType: string
  ) => {
    //Change the permission on user click
    Object.keys(mainMatrix[key1].permissions[key2].options).map((key) => {
      mainMatrix[key1].permissions[key2].options[key].selected = false;
    });
    mainMatrix[key1].permissions[key2].options[permissionType].selected = true;
    setMainMatrix(JSON.parse(JSON.stringify(mainMatrix)));
    props.handlePermissionChanges(mainMatrix, props.matrixType);
  };

  const renderButtonCircle = (
    //Display circle with diffrent color and functions and activate/disable
    key1: string,
    key2: string,
    permissionType: string
  ) => {
    //Creating permission buttons
    let classes = " circleRole " + permissionType + " ";
    if (mainMatrix[key1].permissions[key2].options[permissionType].selected) {
      classes += " Blue ";
    } else if (
      !mainMatrix[key1].permissions[key2].options[permissionType].enabled
    ) {
      classes += " Gray ";
    }
    if (mainMatrix[key1].permissions[key2].options[permissionType].enabled) {
      classes += " borderBlue ";
      return (
        <div
          className={classes}
          onClick={() => {
            addProductPermission(key1, key2, permissionType);
          }}
        >
          {permissionType}
        </div>
      );
    } else {
      classes += " borderGray ";
      return <div className={classes}>{permissionType}</div>;
    }
  };

  const renderMatrix = (key1: string) => {
    if (props.matrixType === "Attributes") {
      const sortedPermissions = Object.entries(
        mainMatrix[key1].permissions
      ).sort((a, b) => {
        const indexA = ORDER_MAP.get(a[1].display) ?? Infinity;
        const indexB = ORDER_MAP.get(b[1].display) ?? Infinity;
        return indexA - indexB;
      });
      mainMatrix[key1].permissions = Object.fromEntries(sortedPermissions);
    }

    //Creating permission button group
    return Object.keys(mainMatrix[key1].permissions).map((key2, subIndex) => (
      <SimpleGrid
        key={subIndex}
        layout={{
          sm: [12],
          lg: [4, 8],
        }}
        spacing={1}
      >
        <Paragraph weight={3}>
          {mainMatrix[key1].permissions[key2].display}
        </Paragraph>
        <div className="permissionBox">
          {renderButtonCircle(key1, key2, "N")}
          {renderButtonCircle(key1, key2, "V")}
          {renderButtonCircle(key1, key2, "E")}
          {renderButtonCircle(key1, key2, "F")}
        </div>
      </SimpleGrid>
    ));
  };

  const sections = Object.keys(mainMatrix).map((key1) => (
    <CollapsibleContainer key={key1}>
      <CollapsibleContainerHeader>
        <Spacing
          spacingLevel={{
            left: 3,
          }}
        >
          {mainMatrix[key1].display}
        </Spacing>
      </CollapsibleContainerHeader>
      <CollapsibleContainerBody>
        <CardContainer>
          <div className="tableViewRole">
            <Spacing
              spacingLevel={{
                top: 4,
              }}
            >
              <SimpleGrid
                columns={{
                  sm: 1,
                  md: 2,
                }}
                spacing={8}
                verticalAlign="center"
              >
                {renderMatrix(key1)}
              </SimpleGrid>
            </Spacing>
          </div>
        </CardContainer>
      </CollapsibleContainerBody>
    </CollapsibleContainer>
  ));

  return (
    // Creating accordion box where there will be different blog of permission on product/user
    <Accordion appearance="primary">{sections}</Accordion>
  );
};
